/**
 * VideoReviewPopup — scroll-triggered CTA for the free personalized video review.
 *
 * Design: a warm bone card — the one light surface on an all-dark site, which is
 * what makes it read at a glance instead of sinking into #0c0c0c. Brand cyan is
 * held back for the button, the eyebrow and the halo so the eye still lands on
 * the action. Barlow throughout, same 88px pill as every other CTA.
 * Sits bottom-LEFT; the cookie banner owns bottom-right, so on desktop the two
 * never meet. On narrow screens they would overlap, so the popup waits for the
 * banner to be answered before it shows at all (see lib/cookieConsent).
 *
 * Timing: appears once the visitor is halfway down the page and has spent
 * MIN_SECONDS on it, and steps aside again over the closing CTA and footer so
 * it never competes with the page's own "Get my free video review" block.
 * Clarity (Oct 2026) showed it closed 35 times for every 2 clicks when it
 * came up right after the hero, so it now waits for readers who have stayed,
 * and a dismissal is remembered for DISMISS_DAYS rather than one tab session.
 */

import { useEffect, useRef, useState } from "react";
import { VIDEO_REVIEW_URL } from "@/lib/cta";
import { readConsent, onConsentResolved } from "@/lib/cookieConsent";
import { isPrerender } from "@/lib/isPrerender";
import { safeLocalStorage } from "@/lib/safeStorage";
import { useIsMobile } from "@/hooks/useIsMobile";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const DISMISS_KEY = "indexed_video_review_popup_dismissed";

/** Show once the visitor has read half the page… */
const SHOW_AFTER_FRACTION = 0.5;
/** …and has been on it this long. */
const MIN_SECONDS = 15;
/** How long a dismissal holds. */
const DISMISS_DAYS = 30;
/** Stand down over the last stretch of the page: the closing CTA plus footer. */
const HIDE_PAST_FRACTION = 0.85;

// Storage can be blocked outright (in-app webviews, site data off), in which
// case reads come back null: better to show the popup than to vanish, and the
// `dismissed` state still hides it for the rest of the pageview.
function wasDismissed(): boolean {
  const at = Number(safeLocalStorage.getItem(DISMISS_KEY));
  return at > 0 && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

function rememberDismissal(): void {
  safeLocalStorage.setItem(DISMISS_KEY, String(Date.now()));
}

export default function VideoReviewPopup() {
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(false);
  const [inScrollZone, setInScrollZone] = useState(false);
  // Mounted-but-transparent for one frame so the entrance transition actually runs.
  const [entered, setEntered] = useState(false);
  // On mobile the cookie banner is effectively full width, so hold off until answered.
  const [bannerClear, setBannerClear] = useState(false);
  const [dwelled, setDwelled] = useState(false);

  useEffect(() => {
    if (isPrerender()) return;
    if (wasDismissed()) { setDismissed(true); return; }

    setBannerClear(readConsent() !== null);
    const unsubscribe = onConsentResolved(() => setBannerClear(true));
    const dwell = window.setTimeout(() => setDwelled(true), MIN_SECONDS * 1000);

    let frame = 0;
    const measure = () => {
      frame = 0;
      const scrolled = window.scrollY;
      const viewport = window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const progress = total > viewport ? (scrolled + viewport) / total : 1;
      setInScrollZone(progress >= SHOW_AFTER_FRACTION && progress < HIDE_PAST_FRACTION);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure(); // handle a restored scroll position on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.clearTimeout(dwell);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      unsubscribe();
    };
  }, []);

  const shouldShow = inScrollZone && dwelled && !dismissed && (!isMobile || bannerClear);

  // Kept mounted through the exit transition so it slides away instead of blinking out.
  const wasShown = useRef(false);
  if (shouldShow) wasShown.current = true;

  useEffect(() => {
    if (!shouldShow) { setEntered(false); return; }
    const t = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(t);
  }, [shouldShow]);

  const dismiss = () => {
    rememberDismissal();
    setDismissed(true);
  };

  const trackClick = () => {
    const w = window as any;
    try {
      w.clarity?.("event", "video_review_popup_click");
      w.fbq?.("track", "Lead");
      w.gtag?.("event", "select_promotion", {
        event_category: "cta",
        event_label: "video_review_popup",
      });
    } catch {
      /* tracking must never block the click through to the form */
    }
  };

  if (!wasShown.current) return null;

  return (
    <div
      className="vrp"
      role="region"
      aria-label="Free video review offer"
      style={{
        transform: entered ? "translate3d(0,0,0)" : "translate3d(-24px, 24px, 0)",
        opacity: entered ? 1 : 0,
        pointerEvents: entered ? "auto" : "none",
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        className="vrp-close"
        aria-label="Dismiss free video review offer"
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>

      <div className="vrp-head">
        <span className="vrp-avatar">
          <img src="/images/team/anirban.webp" alt="" loading="lazy" width="52" height="52" />
          <span className="vrp-play" aria-hidden="true">
            <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
              <path d="M8.2 4.57a.5.5 0 0 1 0 .86L1.25 9.4A.5.5 0 0 1 .5 8.97V1.03a.5.5 0 0 1 .75-.43L8.2 4.57Z" fill="#0c0c0c" />
            </svg>
          </span>
          <span className="vrp-ring" aria-hidden="true" />
        </span>
        <div>
          <span className="vrp-eyebrow">Free video review</span>
          <p className="vrp-title">See what your site is losing.</p>
        </div>
      </div>

      <p className="vrp-body">
        A top-1% SEO records a 10–15 minute walkthrough of your site and shows you
        exactly what's costing you traffic — and what fixing it is worth.
      </p>

      <a href={VIDEO_REVIEW_URL} onClick={trackClick} className="vrp-cta">
        Get my free review
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M3 11L11 3M11 3H5M11 3v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>

      <span className="vrp-note">No call &middot; No pitch &middot; Yours to keep</span>

      <style>{`
        .vrp {
          position: fixed;
          bottom: 20px;
          left: 20px;
          z-index: 9998; /* under the cookie banner (9999) — consent comes first */
          width: min(324px, calc(100vw - 32px));
          box-sizing: border-box;
          padding: 18px;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.55);
          background: linear-gradient(160deg, #f7f4ee 0%, #eae4d9 100%);
          /* The drop shadow separates it from the page; the cyan bloom keeps a
             light card tied to the brand. No backdrop-filter — the surface is
             opaque, so blurring behind it would only cost a compositor layer. */
          box-shadow: 0 14px 46px rgba(0,0,0,0.62), 0 0 58px rgba(87,215,226,0.14);
          font-family: ${ff};
          transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
        }
        .vrp-close {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: rgba(20,31,30,0.07);
          color: rgba(20,31,30,0.45);
          cursor: pointer;
          transition: background 0.18s ease, color 0.18s ease;
        }
        .vrp-close:hover { background: rgba(20,31,30,0.13); color: #141f1e; }

        .vrp-head { display: flex; align-items: center; gap: 12px; padding-right: 22px; }

        .vrp-avatar { position: relative; flex-shrink: 0; width: 52px; height: 52px; }
        .vrp-avatar img {
          width: 52px; height: 52px;
          border-radius: 50%;
          object-fit: cover;
          object-position: center top;
          border: 1px solid rgba(20,31,30,0.14);
          display: block;
        }
        .vrp-play {
          position: absolute;
          right: -2px;
          bottom: -2px;
          width: 20px; height: 20px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          background: linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%);
          border: 2px solid #f2eee6;
        }
        /* Slow halo — reads as "a video is waiting" without pulling focus. */
        .vrp-ring {
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          border: 1px solid rgba(32,176,188,0.45);
          animation: vrp-pulse 2.8s ease-out infinite;
          pointer-events: none;
        }
        @keyframes vrp-pulse {
          0%   { transform: scale(0.94); opacity: 0.7; }
          70%  { transform: scale(1.14); opacity: 0; }
          100% { transform: scale(1.14); opacity: 0; }
        }

        .vrp-eyebrow {
          display: block;
          color: #0a6a74;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }
        .vrp-title {
          margin: 3px 0 0;
          color: #141f1e;
          font-size: 16px;
          font-weight: 700;
          line-height: 1.25;
          letter-spacing: -0.01em;
        }
        .vrp-body {
          margin: 12px 0 0;
          color: rgba(20,31,30,0.64);
          font-size: 13px;
          line-height: 1.6;
        }
        .vrp-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          margin-top: 14px;
          padding: 12px 20px;
          border-radius: 88px;
          border: 1px solid #3cc3ce;
          background: linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%);
          box-shadow: 0 3px 16px rgba(32,176,188,0.35);
          color: #07201f;
          font-size: 14px;
          font-weight: 700;
          text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .vrp-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 7px 24px rgba(32,176,188,0.45);
        }
        .vrp-note {
          display: block;
          margin-top: 9px;
          text-align: center;
          color: rgba(20,31,30,0.6);
          font-size: 11.5px;
        }

        @media (prefers-reduced-motion: reduce) {
          .vrp { transition: opacity 0.2s ease; transform: none !important; }
          .vrp-ring { animation: none; }
          .vrp-cta:hover { transform: none; }
        }
      `}</style>
    </div>
  );
}
